import type {
  CandidateApplicationItem,
  CandidateChecklistActivation,
  CandidateChecklistItem,
  ChecklistOption,
} from '@/services/api/types';

/**
 * Pure helpers for the activation checklist page (T102126038).
 *
 * The answer shapes follow the API's value rules
 * (`candidate-checklist-value.validation.ts` in ems-api):
 * - checkbox / multiple_choice: the option list `{ key, label, val }[]` with `val`
 *   ticked by the candidate (the API seeds it from the template with every `val` false);
 * - dropdown: a string list. The API seeds it with every option and the answer is
 *   the one chosen option, `[choice]`, which is also what the EMS checklist page sends;
 * - number: a number; date: an ISO date string; free_text: a string;
 * - file_upload: the storage key, written by the upload endpoint.
 *
 * Choice items offer the template's option list (`templateItem.values`) when the
 * API returns it, so saving a dropdown answer no longer loses the other options
 * (T102126041); the row's `value` is then read only as the saved answer.
 */

/** Activation states in which the API accepts checklist answers. */
const OPEN_ACTIVATION_STATUSES = ['pending', 'approved'];

/**
 * Normalizes a stored checklist value. Older rows can hold JSON text, or the
 * `{ val: "'…'::jsonb" }` wrapper the EMS checklist pages also unwrap.
 */
export function parseChecklistValue(raw: unknown): unknown {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw;
      }
    }
    return raw;
  }
  if (!Array.isArray(raw) && typeof raw === 'object' && 'val' in raw) {
    const inner = (raw as { val: unknown }).val;
    if (typeof inner === 'string') {
      const match = inner.match(/^'(.+)'::jsonb$/s);
      if (match) {
        try {
          return JSON.parse(match[1]);
        } catch {
          return match[1].replace(/^["']|["']$/g, '');
        }
      }
    }
  }
  return raw;
}

const isOption = (entry: unknown): entry is ChecklistOption =>
  typeof entry === 'object' && entry !== null && !Array.isArray(entry) && 'key' in entry;

/** The option list of a checkbox / multiple-choice item, or null when it has none. */
export function toOptions(raw: unknown): ChecklistOption[] | null {
  const value = parseChecklistValue(raw);
  if (!Array.isArray(value) || value.length === 0 || !value.every(isOption)) return null;
  return value.map((o) => ({
    ...o,
    key: String(o.key),
    label: String(o.label ?? o.key),
    val: o.val === true,
  }));
}

/** The string list of a dropdown item, or null when it has none. */
export function toStringList(raw: unknown): string[] | null {
  const value = parseChecklistValue(raw);
  if (Array.isArray(value)) {
    const list = value.filter((v): v is string | number => typeof v === 'string' || typeof v === 'number').map(String);
    return list.length > 0 ? list : null;
  }
  if (typeof value === 'string' && value.trim() !== '') return [value];
  return null;
}

/**
 * The dropdown choice already saved, if any. The API seeds a dropdown row's
 * `value` with the template's option list, and a save replaces it with
 * `[choice]`, so only a one-entry list can be a saved choice and a longer list
 * is never one.
 *
 * `respondedAt` (ems-api #3667) decides whether the candidate saved: `null`
 * means the row still holds its seed, and a timestamp means the one-entry
 * value is the saved choice even when it equals the seed, as it does for a
 * one-option template (T102126061). API builds without the field fall back to
 * the value: one equal to the whole `templateItem.values` list is the
 * untouched seed, and without that list every one-entry list is read as the
 * saved choice. That fallback cannot tell a saved answer of a one-option
 * dropdown from its seed, so it reads that answer as unanswered.
 */
export function dropdownChoice(item: Pick<CandidateChecklistItem, 'templateItem' | 'value' | 'respondedAt'>): string {
  const list = toStringList(item.value);
  if (!list || list.length !== 1) return '';
  if (item.respondedAt !== undefined) return item.respondedAt ? list[0] : '';
  const template = toStringList(item.templateItem?.values);
  const isSeed = template?.length === 1 && template[0] === list[0];
  return isSeed ? '' : list[0];
}

/** True when a row holds no answer of any shape yet. */
function isEmptyValue(raw: unknown): boolean {
  const value = parseChecklistValue(raw);
  return value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

/**
 * The choices a dropdown item offers (T102126041). The template's option list
 * is the source; the row's `value` holds only the saved choice once the
 * candidate has answered. A saved choice the template no longer lists is kept
 * so the answer stays visible and selected. Without a template list (an API
 * build that does not return `templateItem.values`), the choices come from
 * the row's seeded value as before.
 */
export function dropdownChoices(item: Pick<CandidateChecklistItem, 'templateItem' | 'value' | 'respondedAt'>): string[] {
  const template = toStringList(item.templateItem?.values);
  if (!template) return toStringList(item.value) ?? [];
  const saved = dropdownChoice(item);
  return saved && !template.includes(saved) ? [...template, saved] : template;
}

/**
 * The options a checkbox / multiple-choice item offers, ticked from the saved
 * answer (T102126041). The template's option list gives the options and their
 * labels; the row's saved options give each `val`, matched by `key`. A ticked
 * option the template no longer lists is kept so the answer is not dropped.
 * Returns the row's own options when the template has no list, and null when
 * neither has one or the row holds a non-option answer (a legacy `"true"`
 * checkbox flag), so callers keep their existing fallback.
 */
export function checklistOptions(item: Pick<CandidateChecklistItem, 'templateItem' | 'value'>): ChecklistOption[] | null {
  const saved = toOptions(item.value);
  const template = toOptions(item.templateItem?.values);
  if (!template) return saved;
  if (!saved && !isEmptyValue(item.value)) return null;
  const tickedByKey = new Map<string, boolean>((saved ?? []).map((o) => [o.key, o.val]));
  const templateKeys = new Set(template.map((o) => o.key));
  const options = template.map((o) => ({ ...o, val: tickedByKey.get(o.key) === true }));
  const orphans = (saved ?? []).filter((o) => o.val && !templateKeys.has(o.key));
  return [...options, ...orphans];
}

/** `yyyy-MM-dd` for a date input, from a stored ISO date or timestamp. */
export function toDateInputValue(raw: unknown): string {
  const value = parseChecklistValue(raw);
  if (typeof value !== 'string') return '';
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

/** Storage key of an uploaded file, or '' when nothing is uploaded. */
export function uploadedFileKey(item: Pick<CandidateChecklistItem, 'file' | 'value'>): string {
  if (typeof item.file === 'string' && item.file.trim() !== '') return item.file.trim();
  return typeof item.value === 'string' ? item.value.trim() : '';
}

/** File name shown for an uploaded key (the last path segment). */
export function fileNameFromKey(key: string): string {
  const last = key.split('/').filter(Boolean).pop();
  return last ?? key;
}

/** True when the candidate has given an answer the reviewer can look at. */
export function hasAnswer(item: CandidateChecklistItem): boolean {
  const type = item.templateItem?.itemType;
  switch (type) {
    case 'file_upload':
      return uploadedFileKey(item) !== '';
    case 'checkbox':
    case 'multiple_choice': {
      const options = toOptions(item.value);
      if (options) return options.some((o) => o.val);
      const value = parseChecklistValue(item.value);
      return typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== 'false';
    }
    case 'dropdown':
      return dropdownChoice(item) !== '';
    case 'number': {
      const value = parseChecklistValue(item.value);
      return typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');
    }
    default: {
      const value = parseChecklistValue(item.value);
      return typeof value === 'string' ? value.trim() !== '' : typeof value === 'number';
    }
  }
}

/** True while the API accepts answers for this activation. */
export function isChecklistOpen(activation: CandidateChecklistActivation | null): boolean {
  return !!activation && OPEN_ACTIVATION_STATUSES.includes(String(activation.status).toLowerCase());
}

/** True when the candidate may still change this item: the checklist is open and HR has not approved it. */
export function isItemEditable(item: CandidateChecklistItem, checklistOpen: boolean): boolean {
  return checklistOpen && String(item.status || 'pending').toLowerCase() !== 'approved';
}

export type ChecklistItemState = 'approved' | 'rejected' | 'submitted' | 'todo';

export function itemState(item: CandidateChecklistItem): ChecklistItemState {
  const status = String(item.status || 'pending').toLowerCase();
  if (status === 'approved') return 'approved';
  if (status === 'rejected') return 'rejected';
  return hasAnswer(item) ? 'submitted' : 'todo';
}

/** Checklist rows in the template's order. */
export function sortChecklistItems(items: CandidateChecklistItem[]): CandidateChecklistItem[] {
  return [...items].sort(
    (a, b) => (a.templateItem?.itemNumber ?? Number.MAX_SAFE_INTEGER) - (b.templateItem?.itemNumber ?? Number.MAX_SAFE_INTEGER),
  );
}

/** The API's `message` from an axios error, else the fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

/** HTTP status of an axios error, or 0 when the request never got an answer. */
export function apiErrorStatus(error: unknown): number {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : 0;
}

export interface ApplicationsForJob {
  /** Pipeline row (`candidates`); its `id` is the checklist's candidateId. */
  hiring: CandidateApplicationItem | null;
  /** Application not yet moved into the pipeline (`pending_candidates`). */
  pending: CandidateApplicationItem | null;
}

/**
 * The signed-in candidate's applications to one job posting. The list comes
 * newest first, so the first match of each kind is the latest one.
 */
export function findApplicationsForJob(
  items: CandidateApplicationItem[],
  jobPostingId: string,
): ApplicationsForJob {
  const forJob = items.filter((i) => i.jobPostingId === jobPostingId);
  return {
    hiring: forJob.find((i) => i.recordKind === 'hiring_candidate') ?? null,
    pending: forJob.find((i) => i.recordKind === 'pending_application') ?? null,
  };
}
